import { DOC_VERSION, VALID_RESOLUTIONS } from './graph';
import type { GraphDoc, GraphEdge, GraphNode } from './types';

export const FILE_EXTENSION = '.texdir.json';

/**
 * Parse and structurally validate a document.
 *
 * Documents arrive from disk, from the agent bridge, and from the clipboard, so
 * this is a genuine trust boundary. It checks *shape* only — unknown node types
 * and dangling references are the graph's business, and it keeps them so a file
 * written by a newer build still opens.
 */
export function parseDocument(input: string | unknown): GraphDoc {
  let raw: unknown;
  if (typeof input === 'string') {
    try {
      raw = JSON.parse(input);
    } catch (err) {
      throw new Error(`Not valid JSON: ${(err as Error).message}`);
    }
  } else {
    raw = input;
  }

  if (!isRecord(raw)) throw new Error('Document must be a JSON object');

  const version = Number(raw.version ?? DOC_VERSION);
  if (!Number.isFinite(version) || version < 1) {
    throw new Error(`Unsupported document version: ${String(raw.version)}`);
  }
  if (version > DOC_VERSION) {
    throw new Error(
      `Document version ${version} is newer than this build understands (${DOC_VERSION}). Update texdir to open it.`,
    );
  }

  if (!Array.isArray(raw.nodes)) throw new Error('Document is missing a "nodes" array');
  if (!Array.isArray(raw.edges)) throw new Error('Document is missing an "edges" array');

  const nodes: GraphNode[] = raw.nodes.map((n, i) => parseNode(n, i));
  const ids = new Set<string>();
  for (const n of nodes) {
    if (ids.has(n.id)) throw new Error(`Duplicate node id "${n.id}"`);
    ids.add(n.id);
  }

  const edges: GraphEdge[] = raw.edges.map((e, i) => parseEdge(e, i));

  const resolution = Number(raw.resolution ?? 512);
  return {
    version: DOC_VERSION,
    name: typeof raw.name === 'string' && raw.name.trim() ? raw.name : 'Untitled',
    resolution: (VALID_RESOLUTIONS as readonly number[]).includes(resolution) ? resolution : 512,
    filtering: raw.filtering === 'nearest' ? 'nearest' : 'linear',
    nodes,
    edges,
  };
}

function parseNode(raw: unknown, index: number): GraphNode {
  if (!isRecord(raw)) throw new Error(`nodes[${index}] must be an object`);
  const id = raw.id;
  const type = raw.type;
  if (typeof id !== 'string' || !id) throw new Error(`nodes[${index}] is missing an "id"`);
  if (typeof type !== 'string' || !type)
    throw new Error(`nodes[${index}] ("${id}") is missing a "type"`);

  const node: GraphNode = {
    id,
    type,
    x: finite(raw.x, 0),
    y: finite(raw.y, 0),
    params: isRecord(raw.params) ? { ...(raw.params as GraphNode['params']) } : {},
  };
  if (typeof raw.title === 'string' && raw.title.trim()) node.title = raw.title;
  if (raw.collapsed === true) node.collapsed = true;
  return node;
}

function parseEdge(raw: unknown, index: number): GraphEdge {
  if (!isRecord(raw)) throw new Error(`edges[${index}] must be an object`);
  const { id, from, to, toPort } = raw;
  if (typeof from !== 'string' || typeof to !== 'string') {
    throw new Error(`edges[${index}] needs string "from" and "to"`);
  }
  if (typeof toPort !== 'string' || !toPort) {
    throw new Error(`edges[${index}] needs a "toPort"`);
  }
  return {
    id: typeof id === 'string' && id ? id : `e_${index}`,
    from,
    fromPort: typeof raw.fromPort === 'string' && raw.fromPort ? raw.fromPort : 'out',
    to,
    toPort,
  };
}

export function serializeDocument(doc: GraphDoc): string {
  return `${JSON.stringify(doc, null, 2)}\n`;
}

/** Filesystem-safe name derived from the document title. */
export function documentFilename(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `${slug || 'untitled'}${FILE_EXTENSION}`;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function finite(v: unknown, fallback: number): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}
